(() => {
  'use strict';
  if (customElements.get('bb-klarna-financing')) return;
  const sdkUrl = 'https://js.klarna.com/web-sdk/v1/klarna.js';
  let sdkPromise, sdkIdentity, activated = false, noticeId = 0;
  function loadSdk(clientId, environment) {
    const identity = `${environment}:${clientId}`;
    if (sdkIdentity && sdkIdentity !== identity) return Promise.reject(new Error('Klarna configuration conflict'));
    if (sdkPromise) return sdkPromise;
    sdkIdentity = identity;
    sdkPromise = new Promise((resolve, reject) => {
      let script = document.querySelector(`script[src="${sdkUrl}"]`);
      if (script && (script.dataset.clientId !== clientId || script.dataset.environment !== environment)) {
        reject(new Error('Klarna configuration conflict')); return;
      }
      if (script && window.Klarna?.OnsiteMessaging) return resolve();
      const fresh = !script;
      if (fresh) {
        script = document.createElement('script'); script.src = sdkUrl; script.async = true;
        script.dataset.clientId = clientId; script.dataset.environment = environment;
      }
      const fail = () => { if (fresh) script.remove(); reject(new Error('Klarna unavailable')); };
      const timer = setTimeout(fail, 12000);
      script.addEventListener('load', () => { clearTimeout(timer); resolve(); }, { once: true });
      script.addEventListener('error', () => { clearTimeout(timer); fail(); }, { once: true });
      if (fresh) document.head.appendChild(script);
    }).catch(error => { sdkPromise = undefined; throw error; });
    return sdkPromise;
  }
  class Financing extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('template');
      this.offer = this.querySelector('[data-bb-klarna-offer]');
      this.button = this.querySelector('[data-bb-klarna-load]');
      this.status = this.querySelector('[data-bb-klarna-status]');
      this.total = this.querySelector('[data-bb-klarna-total-value]');
      this.english = this.dataset.locale === 'en-DE';
      const notice = this.querySelector('[data-bb-klarna-consent]');
      notice.id = `bb-financing-consent-${++noticeId}`;
      this.button.setAttribute('aria-describedby', notice.id);
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.button.addEventListener('click', () => {
        activated = true; window.dispatchEvent(new CustomEvent('bb:klarna-activated'));
      }, options);
      window.addEventListener('bb:klarna-activated', () => this.renderOffer(), options);
      this.form = document.getElementById(this.dataset.form);
      this.section = this.closest('.shopify-section');
      try { this.variants = JSON.parse(this.querySelector('[data-bb-klarna-variants]')?.textContent || '{}'); }
      catch { this.variants = {}; }
      const schedule = () => {
        this.dataset.loaded = 'false';
        clearTimeout(this.syncTimer);
        this.syncTimer = setTimeout(() => this.syncProduct(), 120);
      };
      if (this.dataset.context === 'product') {
        this.section?.addEventListener('change', schedule, options);
        this.section?.addEventListener('click', event => {
          if (event.target.closest('[data-bb-unit-button]')) schedule();
        }, options);
        const id = this.form?.querySelector('[name="id"]');
        if (id) {
          this.observer = new MutationObserver(schedule);
          this.observer.observe(id, { attributes: true, attributeFilter: ['value'] });
        }
        this.syncProduct();
        this.syncTimer = setTimeout(() => this.syncProduct(), 150);
      } else this.setAmount(Number(this.dataset.amount), this.dataset.ready === 'true');
      if (this.dataset.autoLoad === 'true') {
        this.visibilityObserver = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.visibilityObserver.disconnect();
          activated = true;
          this.renderOffer();
        }, { rootMargin: '400px 0px' });
        this.visibilityObserver.observe(this);
      }

    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.visibilityObserver?.disconnect();
      clearTimeout(this.syncTimer); clearInterval(this.renderTimer);
      this.version = (this.version || 0) + 1;
    }
    syncProduct() {
      if (!this.isConnected || !this.form) return;
      const id = this.form.querySelector('[name="id"]')?.value;
      const variant = this.variants[id];
      const picker = this.section?.querySelector(`bb-offer-picker[form="${CSS.escape(this.dataset.form)}"]`);
      const tier = picker?.querySelector('input[name^="bb-offer"]:checked');
      const qty = Number(tier?.dataset.qty || this.form.querySelector('[name="quantity"]')?.value || this.section?.querySelector(`[name="quantity"][form="${CSS.escape(this.dataset.form)}"]`)?.value || 1);
      const pct = Number(tier?.dataset.discount || 0);
      const discount = Math.floor(Number(variant?.price) * pct / 100);
      const amount = (Number(variant?.price) - discount) * qty;
      const ready = Boolean(variant?.available) && Number.isSafeInteger(qty) && qty > 0 && pct >= 0 && pct <= 100 && !this.form.querySelector('[name="selling_plan"]')?.value;
      this.setAmount(amount, ready);
    }
    setAmount(amount, ready) {
      ready = ready && Number.isSafeInteger(amount) && amount > 0;
      const changed = this.dataset.amount !== String(amount) || this.hidden;
      this.hidden = !ready; this.dataset.ready = String(ready); this.dataset.amount = String(amount);
      if (!ready) { clearInterval(this.renderTimer); return; }
      this.total.textContent = new Intl.NumberFormat(this.english ? 'en-DE' : 'de-DE', { style: 'currency', currency: 'EUR' }).format(amount / 100);
      if (activated && (changed || this.dataset.loaded !== 'true')) this.renderOffer();
    }
    async renderOffer() {
      if (!activated || this.hidden || !this.dataset.clientId) return;
      const version = this.version = (this.version || 0) + 1;
      clearInterval(this.renderTimer); this.dataset.loaded = 'false';
      this.button.hidden = this.dataset.autoLoad === 'true'; this.button.disabled = true; this.dataset.monthly = 'false';
      this.status.textContent = this.english ? 'Loading Klarna options…' : 'Klarna-Raten werden geladen …';
      try {
        await loadSdk(this.dataset.clientId, this.dataset.environment);
        if (!this.isConnected || version !== this.version) return;
        // Replace the placement to avoid showing a previous amount's rate.
        this.offer.replaceChildren(this.template.content.cloneNode(true));
        const placement = this.offer.querySelector('klarna-placement');
        placement.dataset.purchaseAmount = this.dataset.amount;
        window.Klarna?.OnsiteMessaging?.refresh();
        let checks = 0;
        this.renderTimer = setInterval(() => {
          if (!this.isConnected || version !== this.version) return clearInterval(this.renderTimer);
          const message = placement.shadowRoot?.querySelector('[part~="osm-message"]')?.textContent?.trim();
          if (message) {
            clearInterval(this.renderTimer); this.dataset.loaded = 'true'; this.status.textContent = '';
            this.dataset.monthly = String(/(?:€|EUR)\s*\/\s*(?:Monat|month)/i.test(message) && !/^(?:ab|from|as low as)\b/i.test(message));
            this.button.hidden = true; this.button.disabled = false;
          } else if (++checks >= 80) { clearInterval(this.renderTimer); this.unavailable(); }
        }, 150);
      } catch { if (this.isConnected && version === this.version) this.unavailable(); }
    }
    unavailable() {
      this.dataset.loaded = 'false'; this.button.hidden = false; this.button.disabled = false;
      this.status.textContent = this.english ? 'Klarna cannot display options right now. Check availability at payment.' : 'Klarna kann gerade keine Raten anzeigen. Prüfe die Optionen bei der Zahlung.';
    }
  }
  customElements.define('bb-klarna-financing', Financing);
})();
